import { useEffect } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { useInstallationContext } from '../../../../context/installation';
import { ChannelsGetAllQueryVariables, Query } from '../../../../data-access/gql-types/graphql';
import { CHANNELS_GET_ALL } from '../../../../data-access/queries/channels';
import { ON_USES_DEPLETED } from '../../../../data-access/subscriptions/on-uses-depleted';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import { useChannelsStore } from '../../store/channels';
import { useChannelsStateStore } from '../../store/channels-state';
import { ChannelType, ChannelStateType } from '../../types';
import { parseChannel, parseChannelState } from '../../utils/channels/helpers';
import { useUpdateCommonLavvaState } from '../../utils/channels/subscriptions/common-lavva-state';

export const useFetchChannels = () => {
  const { selectedInstallationId, skipLavvaFetch, currentInstallationLoading } = useInstallationContext();
  const { setChannelList } = useDevicesAndChannelsContext();
  const setAllChannelsState = useChannelsStateStore((s) => s.setAllChannelsState);
  const { shouldRefetchChannels, resetRefetchChannels, setChannelsLoading } = useChannelsStore((s) => s);
  const { usesDepleted } = useUpdateCommonLavvaState();

  const processChannelsData = (data: Query) => {
    if (data?.allUserChannels && selectedInstallationId) {
      const newChannelList: ChannelType[] = [];
      let channelStates: Record<string, ChannelStateType> = {};

      data.allUserChannels.forEach((userChannel) => {
        if (userChannel.installationId !== selectedInstallationId) return;
        const channel = parseChannel(userChannel);
        const state = parseChannelState(userChannel);

        if (channel) {
          newChannelList.push(channel);

          if (state) {
            channelStates = { ...channelStates, [channel.id]: state };
          }
        }
      });

      setChannelList(newChannelList);
      setAllChannelsState(channelStates);
    }
  };

  const {
    data: channelsData,
    loading,
    refetch,
  } = useQuery<Query, ChannelsGetAllQueryVariables>(CHANNELS_GET_ALL, {
    variables: { installationId: selectedInstallationId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: skipLavvaFetch,
  });

  useEffect(() => {
    if (channelsData) processChannelsData(channelsData);
  }, [channelsData]);

  useEffect(() => {
    if (currentInstallationLoading) setChannelsLoading(true);
    else {
      if (!selectedInstallationId) setChannelsLoading(false);
      else {
        setChannelsLoading(loading || skipLavvaFetch);
      }
    }
  }, [loading, skipLavvaFetch, currentInstallationLoading, selectedInstallationId]);

  const { data } = useSubscription(ON_USES_DEPLETED, {
    variables: { installationId: selectedInstallationId },
    skip: skipLavvaFetch,
  });

  useEffect(() => {
    if (data) usesDepleted(data.onUsesDepleted);
  }, [data]);

  const fetchChannels = async () => {
    resetRefetchChannels();

    try {
      const { data } = await refetch({ installationId: selectedInstallationId });

      if (data) processChannelsData(data);
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    if (shouldRefetchChannels && selectedInstallationId) fetchChannels();
  }, [shouldRefetchChannels, selectedInstallationId, fetchChannels]);
};
